import { useRef, useState } from "react";
import { TID } from "@/constants/testIds";
import { Download, MapPin, Plane, Share2, Copy, Check } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

/**
 * BoardingPass — flight-ticket-style visitor pass for the Chapter III launch.
 *
 * Actions:
 *  - **Download PNG**: `html-to-image` renders the wrapper node to a PNG data URL
 *    and triggers a browser download.
 *  - **Share on WhatsApp**: opens a `wa.me/?text=...` deep-link with a message
 *    and a public shareable pass URL (`/pass/{registration_id}`).
 *  - **Copy link**: copies the same public pass URL to the clipboard.
 */
export default function BoardingPass({ registration, publicView = false }) {
  const wrapperRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { ticket, payload } = registration;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
    `PALIHILLCITY|${ticket.ticket_no}|${payload.name}`
  )}&size=180x180&margin=8&bgcolor=F4F4EF&color=0B4A3D&format=svg`;

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/pass/${registration.id}`
      : "";
  const waText = encodeURIComponent(
    `I'm on the guest list for Pali Hill City · Chapter III on Sep 12–13, 2026. Above the clouds, at Pali. Here's my boarding pass — ${shareUrl}`
  );
  const waHref = `https://wa.me/?text=${waText}`;

  const download = async () => {
    if (!wrapperRef.current || downloading) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(wrapperRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#F4F4EF",
        // Skip the action-strip so it isn't captured in the PNG.
        filter: (node) => !node.dataset || node.dataset.excludeFromCapture !== "true",
      });
      const link = document.createElement("a");
      link.download = `PaliHillCity-${ticket.ticket_no}.png`;
      link.href = dataUrl;
      link.click();
      track("download_pass", { ticket: ticket.ticket_no });
      toast.success("Boarding pass saved.");
    } catch (err) {
      toast.error("Couldn't generate the pass image. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy the link. Long-press to copy manually.");
    }
  };

  return (
    <div className="fade-up">
      <div
        data-testid={TID.boardingPass}
        ref={wrapperRef}
        className="relative bg-white border border-forest/15 rounded-2xl overflow-hidden shadow-[0_30px_70px_-30px_rgba(11,74,61,0.55)]"
      >
        {/* Top strip */}
        <div className="bg-forest text-cream px-5 md:px-7 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full grid place-items-center bg-lime text-void">
              <Plane size={16} className="-rotate-45" />
            </div>
            <div className="leading-tight">
              <div className="eyebrow text-lime">Boarding Pass</div>
              <div className="font-display text-base">Pali Hill City · Chapter III</div>
            </div>
          </div>
          <div className="text-right text-xs text-cream/70">
            <div className="eyebrow text-lime">Class</div>
            <div className="font-display text-base">{ticket.class || "Guest"}</div>
          </div>
        </div>

        {/* Main body — two columns split by a dashed perforation */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_180px] relative">
          {/* Left: passenger info */}
          <div className="p-5 md:p-7 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <PassField label="Passenger">
                <div className="font-display text-forest text-xl leading-tight uppercase tracking-wide">{payload.name}</div>
              </PassField>
              <PassField label="Flight">
                <div className="font-display text-forest text-xl leading-tight">{ticket.flight}</div>
              </PassField>
            </div>

            <div className="grid grid-cols-3 gap-4 items-end">
              <PassField label="From">
                <div className="font-display text-forest text-lg leading-tight uppercase">{payload.city || "Anywhere"}</div>
                <div className="text-[10px] text-forest/50 tracking-widest uppercase">City</div>
              </PassField>
              <div className="flex flex-col items-center justify-end pb-1">
                <div className="w-full flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-forest/40" />
                  <span className="h-px flex-1 bg-forest/25" />
                  <Plane size={14} className="text-teal -rotate-45" />
                  <span className="h-px flex-1 bg-forest/25" />
                  <span className="w-1 h-1 rounded-full bg-forest/40" />
                </div>
              </div>
              <PassField label="To">
                <div className="font-display text-forest text-lg leading-tight uppercase">Pali Ridge</div>
                <div className="text-[10px] text-forest/50 tracking-widest uppercase inline-flex items-center gap-1">
                  <MapPin size={9} /> Raigad, MH
                </div>
              </PassField>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <PassField label="Boarding Date">
                <div className="font-display text-forest text-base leading-tight">{ticket.boarding_date}, 2026</div>
              </PassField>
              <PassField label="Gate">
                <div className="font-display text-forest text-base leading-tight">{ticket.gate}</div>
              </PassField>
              <PassField label="Seat">
                <div className="font-display text-forest text-base leading-tight">{ticket.seat}</div>
              </PassField>
              <PassField label="Seq.">
                <div className="font-display text-forest text-base leading-tight">#{ticket.sequence}</div>
              </PassField>
            </div>
          </div>

          {/* Perforation */}
          <div className="hidden md:flex items-center relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-offwhite" />
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-offwhite" />
            <div className="h-full border-l border-dashed border-forest/30 mx-2" />
          </div>

          {/* Right stub: QR + ticket no. */}
          <div className="bg-cream p-5 flex flex-col items-center justify-center gap-3 border-t md:border-t-0 md:border-l border-dashed border-forest/25">
            <div className="eyebrow text-forest/60">Present at gate</div>
            <img
              src={qrSrc}
              alt="Boarding pass QR"
              width={140}
              height={140}
              className="rounded-md bg-offwhite p-1"
              crossOrigin="anonymous"
            />
            <div className="text-center">
              <div className="eyebrow text-forest/50">Ticket No.</div>
              <div
                data-testid={TID.boardingTicketNo}
                className="font-display text-forest text-[15px] tracking-tight"
              >
                {ticket.ticket_no}
              </div>
            </div>
          </div>
        </div>

        {/* Footer strip (kept inside capture — pure text, no buttons) */}
        <div className="bg-forest/[0.04] px-5 md:px-7 py-3 text-[11px] text-forest/60">
          <span>Please arrive 30 min before golden hour. Save this pass to your phone.</span>
        </div>
      </div>

      {/* Action row — excluded from the PNG capture */}
      {!publicView && (
        <div
          data-exclude-from-capture="true"
          className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
        >
          <button
            data-testid={TID.boardingDownload}
            onClick={download}
            disabled={downloading}
            className="btn-primary text-sm justify-center"
          >
            <Download size={15} />
            {downloading ? "Preparing\u2026" : "Download pass"}
          </button>

          <a
            data-testid={TID.boardingShareWhatsapp}
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("share_whatsapp", { ticket: ticket.ticket_no })}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-forest text-forest px-5 py-2.5 text-sm font-body font-medium hover:bg-forest hover:text-cream transition-colors"
          >
            <Share2 size={15} />
            Share on WhatsApp
          </a>

          <button
            data-testid={TID.boardingCopyLink}
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-2 text-sm text-forest/70 hover:text-forest transition-colors self-center sm:ml-auto"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy pass link"}
          </button>
        </div>
      )}

      {!publicView && (
        <p className="mt-3 text-[11px] text-forest/45 leading-relaxed">
          Sharing on WhatsApp will attach a link to a view-only page of your boarding pass. The QR code above is unique to your ticket and will be scanned at the gate.
        </p>
      )}
    </div>
  );
}

function PassField({ label, children }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] text-forest/50 tracking-widest uppercase">{label}</div>
      {children}
    </div>
  );
}
